import React, { ChangeEvent } from "react";
import { IDataDefinition } from "../../../rule-engine-core/Common/Data/Definitions/IDataDefinition";
import { ValidationIssue } from "../../../rule-engine-core/Common/IDesignValidationProvider";
import { FormControl } from "../FormControl";
import { PropertyEditorSelect } from "../../../CommonUI/PropertyEditing/PropertyEditorSelect";
import { PropertyEditorText } from "../../../CommonUI/PropertyEditing/PropertyEditorText";
import { validateVariableName } from "../../../rule-engine-core/Common/DataValidationUtilities";
import { Box, Button, IconButton, TextField, Typography } from "@mui/material";
import { ObjectDataDefinition } from "../../../rule-engine-core/Common/Data/Definitions/ObjectDataDefinition";
import { StringDataDefinition } from "../../../rule-engine-core/Common/Data/Definitions/StringDataDefinition";
import { NumberDataDefinition } from "../../../rule-engine-core/Common/Data/Definitions/NumberDataDefinition";
import UIChart from "../../../CommonUI/BuildlingBlocks/UIChart";
import { PropertyEditorBoolean } from "../../../CommonUI/PropertyEditing/PropertyEditorBoolean";
import {
    chartNestedSeriesWithExtraData,
    chartNestedSeriesWithoutExtraData,
    chartNonNestSeries,
    chartNonSeries,
} from "../Testing/ChartSameple";
import { PropertyEditorColor } from "../../../CommonUI/PropertyEditing/PropertyEditorColor";
import { PropertyEditorFloat } from "../../../CommonUI/PropertyEditing/PropertyEditorFloat";
import styled from "styled-components";
import { RemoveCircleOutline } from "@mui/icons-material";
import { DataUtilities } from "../../../rule-engine-core/Common/DataUtilities";
import { StateManager } from "../../../rule-engine-core/Common/StateManager";
import { UIStyleConstants } from "../../../rule-engine-core/Common/UIStyleConstants";
import { PropertyEditorCollapse } from "../../../CommonUI/PropertyEditing/PropertyEditorCollapse";
import { UISpacedBoxContent } from "../../../CommonUI/BuildlingBlocks/UISpacedBoxContent";
import { UILabel } from "../../../CommonUI/BuildlingBlocks/UILabel";

export class BarChartFormControl extends FormControl {
    readonly __type: string = "Bar Chart";
    label?: string;
    chartType: string;
    dataName?: string;

    allowSeries?: boolean;
    seriesNames?: string[];
    stackBars?: boolean;
    seriesDataType?: string;

    xAxisLabel?: string;
    yAxisLabel?: string;

    showLegend: boolean;
    legendPosition?: string;

    render3D: boolean;

    colorType: string;
    color?: string | string[];

    showXYAxis?: boolean;

    allowActions?: boolean;
    showTooltip?: boolean;

    setLowerBounds?: boolean;
    lowerBounds?: number;

    setUpperBounds?: boolean;
    upperBounds?: number;

    numberFormatter?: string;
    customNumberFormatter?: string;

    cursor: boolean;

    showIncrements: boolean;
    increment: string;

    constructor() {
        super();
        this.chartType = "BarChart";
        this.label = "Configure Label";
        this.colorType = "Random";
        this.showLegend = false;
        this.render3D = false;
        this.legendPosition = "No";
        // this.tooltip = false;
        this.cursor = false;
        this.showXYAxis = true;
        this.numberFormatter = "Normal";
        this.customNumberFormatter = "#";

        this.lowerBounds = 0;
        this.upperBounds = 100;
        this.setLowerBounds = false;
        this.setUpperBounds = false;

        this.allowSeries = false;
        this.seriesNames = ["amount", "count"];
        this.seriesDataType = "Nested";

        this.showIncrements = false;
        this.increment = "100";

        this.updateDataDefinition = this.updateDataDefinition.bind(this);

        StateManager.addObserver(this, () => this.updateDataDefinition());
    }

    getDesignValidationIssues(): ValidationIssue[] {
        let issues: ValidationIssue[] = [];

        if (!this.dataName) {
            issues.push(new ValidationIssue("Data name is required", undefined, this.id));
        } else {
            let varIssue = validateVariableName(this.dataName);

            if (varIssue) {
                varIssue.dataName = this.dataName;
                varIssue.elementId = this.id;
                issues.push(varIssue);
            }
        }

        return issues;
    }

    getRuntimeValidationIssues(): ValidationIssue[] {
        return [];
    }

    getAddedData(): IDataDefinition | IDataDefinition[] | undefined {
        return undefined;
    }

    getDataDefinition(): IDataDefinition | undefined {
        if (!this.dataName) return;
        let chartDataDef: ObjectDataDefinition;
        chartDataDef = new ObjectDataDefinition(this.dataName);

        if (!this.allowSeries) {
            chartDataDef.list = true;
            const labelDef = new StringDataDefinition("label");
            const valueDef = new NumberDataDefinition("value");
            chartDataDef.fields.push(labelDef);
            chartDataDef.fields.push(valueDef);
            if (this.colorType === "Custom") {
                const colorDef = new StringDataDefinition("color");
                chartDataDef.fields.push(colorDef);
            }
            return chartDataDef;
        }

        if (this.seriesDataType === "Nested") {
            chartDataDef.list = true;
            const labelDef = new StringDataDefinition("label");
            chartDataDef.fields.push(labelDef);
            if (this.colorType === "Custom") {
                if (this.seriesNames) {
                    for (const series of this.seriesNames) {
                        const seriesDataDef = new ObjectDataDefinition(series);
                        const valueDef = new NumberDataDefinition("value");
                        const colorDef = new StringDataDefinition("color");
                        seriesDataDef.fields.push(valueDef);
                        seriesDataDef.fields.push(colorDef);
                        chartDataDef.fields.push(seriesDataDef);
                    }
                }
            } else {
                if (this.seriesNames) {
                    for (const series of this.seriesNames) {
                        const seriesDataDef = new NumberDataDefinition(series);
                        chartDataDef.fields.push(seriesDataDef);
                    }
                }
            }
            return chartDataDef;
        }

        if (this.seriesNames) {
            for (const series of this.seriesNames) {
                const seriesDataDef = new ObjectDataDefinition(series);
                seriesDataDef.list = true;
                const labelDef = new StringDataDefinition("label");
                seriesDataDef.fields.push(labelDef);
                const valueDef = new NumberDataDefinition("value");
                seriesDataDef.fields.push(valueDef);
                if (this.colorType === "Custom") {
                    const colorDef = new StringDataDefinition("color");
                    seriesDataDef.fields.push(colorDef);
                }
                chartDataDef.fields.push(seriesDataDef);
            }
        }

        return chartDataDef;
    }

    updateDataDefinition(previous?: string) {
        const dataDef = this.getFormContext()?.form.getInputDefinition();
        const findKey = previous ? previous : this.dataName!;
        if (dataDef) {
            let tempFields = dataDef.fields;
            if (findKey) {
                tempFields = dataDef.fields.filter((field) => field.name !== findKey);
            }
            const chartDataDef = this.getDataDefinition();
            if (chartDataDef) {
                tempFields.push(chartDataDef);
            }

            Reflect.set(dataDef, "fields", tempFields);
            StateManager.propertyChanged(dataDef, "fields");
        }
    }

    updateChartData(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
        const value = e.target.value;
        const previous = this.dataName;
        this.dataName = value;
        this.updateDataDefinition(previous);
        StateManager.changed(this);
    }

    getChartDefSampleValue(): any {
        if (this.allowSeries) {
            if (this.seriesDataType === "Nested") {
                if (this.colorType === "Custom") {
                    return chartNestedSeriesWithExtraData;
                } else {
                    return chartNestedSeriesWithoutExtraData;
                }
            } else {
                return chartNonNestSeries;
            }
        }
        return chartNonSeries;
    }

    getChartData() {
        const dataDef = this.getFormContext()?.form.getInputDefinition();
        if (!dataDef || !this.dataName) {
            return this.getChartDefSampleValue();
        }
        const data = Reflect.get(dataDef.value, this.dataName) as any[];
        if (!data) return this.getChartDefSampleValue();
        return data;
    }

    render() {
        return (
            <>
                {this.label && <UILabel variant={"form_label"}>{this.label}</UILabel>}
                {this.getChartData() &&
                    (this.getChartData().length > 0 || Object.keys(this.getChartData()).length > 0) && (
                        <div data-testid={this.dataName}>
                            <UIChart
                                options={{
                                    data: this.getChartData(),
                                    category: this.xAxisLabel,
                                    value: this.yAxisLabel,
                                    chartType: this.chartType,
                                    allowSeries: this.allowSeries,
                                    seriesNames: this.seriesNames,
                                    legendPosition: this.legendPosition?.toLowerCase(),
                                    show3D: this.render3D,
                                    cursor: this.cursor,
                                    colorType: this.colorType,
                                    color: this.color,
                                    showXYAxis: this.showXYAxis,
                                    numberFormatter: this.numberFormatter,
                                    customNumberFormatter: this.customNumberFormatter,
                                    showToolTip: this.showTooltip,
                                    setLowerBounds: this.setLowerBounds,
                                    lowerBounds: this.lowerBounds,
                                    setUpperBounds: this.setUpperBounds,
                                    upperBounds: this.upperBounds,
                                    stackBars: this.stackBars,
                                    seriesDataType: this.seriesDataType,
                                    showIncrements: this.showIncrements,
                                    increment: parseInt(this.increment),
                                }}
                            />
                        </div>
                    )}
            </>
        );
    }

    renderEditUI(): JSX.Element | null {
        return (
            <UISpacedBoxContent>
                <PropertyEditorText
                    editObject={this}
                    label={"Label"}
                    propertyName="label"
                />

                <TextField
                    label="Dataname"
                    value={this.dataName}
                    onChange={(e) => this.updateChartData(e)}
                    variant="outlined"
                />

                <PropertyEditorBoolean
                    label="Allow Series"
                    propertyName="allowSeries"
                    editObject={this}
                />

                <PropertyEditorCollapse
                    editObject={this}
                    collapseLogicOrField={"allowSeries"}
                >
                    <StyledContainer>
                        <Typography>Series</Typography>
                        {this.seriesNames?.map((seriesName, key) => (
                            <TextField
                                key={key}
                                value={seriesName}
                                onChange={(e) => {
                                    this.seriesNames![key] = e.target.value;
                                    this.updateDataDefinition();
                                }}
                                variant="outlined"
                                InputProps={{
                                    endAdornment: (
                                        <IconButton
                                            onClick={() => {
                                                this.seriesNames?.splice(key, 1);
                                                this.updateDataDefinition();
                                            }}
                                            size="large"
                                        >
                                            <RemoveCircleOutline />
                                        </IconButton>
                                    ),
                                }}
                            />
                        ))}
                        <Button
                            variant="contained"
                            sx={{ background: UIStyleConstants.BLUE_COLOR }}
                            onClick={() => {
                                this.seriesNames!.push("series_" + (this.seriesNames!.length + 1));
                                this.updateDataDefinition();
                            }}
                        >
                            Add Series
                        </Button>
                    </StyledContainer>

                    <PropertyEditorBoolean
                        label="Stacked"
                        propertyName="stackBars"
                        editObject={this}
                    />

                    <PropertyEditorSelect
                        label="Nested"
                        options={["Nested", "Non Nested"]}
                        propertyName="seriesDataType"
                        editObject={this}
                    />
                </PropertyEditorCollapse>

                <PropertyEditorText
                    editObject={this}
                    label={"X Axis Label"}
                    propertyName="xAxisLabel"
                />

                <PropertyEditorText
                    editObject={this}
                    label={"Y Axis Label"}
                    propertyName="yAxisLabel"
                />

                <PropertyEditorBoolean
                    label="Render 3D"
                    propertyName="render3D"
                    editObject={this}
                />

                <PropertyEditorBoolean
                    label="Show X/Y Axis"
                    propertyName="showXYAxis"
                    editObject={this}
                />

                <PropertyEditorBoolean
                    label="Set Lower Bounds"
                    propertyName="setLowerBounds"
                    editObject={this}
                />

                <PropertyEditorCollapse
                    editObject={this}
                    collapseLogicOrField={"setLowerBounds"}
                >
                    <PropertyEditorFloat
                        editObject={this}
                        label="Lower Bounds Value"
                        propertyName="lowerBounds"
                    />
                </PropertyEditorCollapse>

                <PropertyEditorBoolean
                    label="Set Upper Bounds"
                    propertyName="setUpperBounds"
                    editObject={this}
                />

                <PropertyEditorCollapse
                    editObject={this}
                    collapseLogicOrField={"setUpperBounds"}
                >
                    <PropertyEditorFloat
                        editObject={this}
                        label="Upper Bounds Value"
                        propertyName="upperBounds"
                    />
                </PropertyEditorCollapse>

                <PropertyEditorBoolean
                    label="Show Increaments Lines"
                    propertyName="showIncrements"
                    editObject={this}
                />

                <PropertyEditorCollapse
                    editObject={this}
                    collapseLogicOrField={() =>
                        this.showIncrements === true && this.setUpperBounds === true && this.setLowerBounds === true
                    }
                    observeProperties={["showIncrements", "setUpperBounds", "setLowerBounds"]}
                >
                    <PropertyEditorSelect
                        editObject={this}
                        options={[
                            "5",
                            "10",
                            "20",
                            "25",
                            "50",
                            "100",
                            "200",
                            "250",
                            "500",
                            "1000",
                            "2000",
                            "2500",
                            "5000",
                            "10000",
                        ]}
                        label="Increment Value"
                        propertyName="increment"
                    />
                </PropertyEditorCollapse>

                <PropertyEditorSelect
                    label="Show Legend"
                    options={["No", "Top", "Bottom", "Left", "Right"]}
                    propertyName="legendPosition"
                    editObject={this}
                />

                <PropertyEditorSelect
                    label="Number Formatter"
                    options={["Normal", "Currency", "BigNumber", "Byte", "Percent", "Custom"]}
                    propertyName="numberFormatter"
                    editObject={this}
                />

                <PropertyEditorCollapse
                    editObject={this}
                    collapseLogicOrField={() => this.numberFormatter === "Custom"}
                    observeProperties={["numberFormatter"]}
                >
                    <PropertyEditorText
                        editObject={this}
                        label={"Custom Number Formatter"}
                        propertyName="customNumberFormatter"
                    />
                </PropertyEditorCollapse>

                <PropertyEditorSelect
                    label="Color Schema"
                    options={["Custom", "Mono", "Random"]}
                    propertyName="colorType"
                    editObject={this}
                />

                <PropertyEditorCollapse
                    editObject={this}
                    collapseLogicOrField={() => this.colorType === "Mono"}
                    observeProperties={["colorType"]}
                >
                    <PropertyEditorColor
                        label="Color"
                        propertyName="color"
                        editObject={this}
                    />
                </PropertyEditorCollapse>

                <PropertyEditorBoolean
                    label="Active Pointer"
                    propertyName="cursor"
                    editObject={this}
                />

                <PropertyEditorBoolean
                    label="Allow Actions"
                    propertyName="allowActions"
                    editObject={this}
                />

                <PropertyEditorBoolean
                    label="Show Tooltip"
                    propertyName="showTooltip"
                    editObject={this}
                />
            </UISpacedBoxContent>
        );
    }

    getStorageData(): object {
        let retData = super.getStorageData();

        retData = DataUtilities.addStorageData(
            this,
            retData,
            "__type",
            "label",
            "chartType",
            "dataName",
            "allowSeries",
            "seriesNames",
            "stackBars",
            "seriesDataType",
            "xAxisLabel",
            "yAxisLabel",
            "showLegend",
            "legendPosition",
            "render3D",
            "colorType",
            "color",
            "showXYAxis",
            "allowActions",
            "showTooltip",
            "setLowerBounds",
            "lowerBounds",
            "setUpperBounds",
            "upperBounds",
            "numberFormatter",
            "customNumberFormatter",
            "cursor",
            "showIncrements",
            "increment",
        );
        return retData;
    }

    setStorageData(data: object): void {
        super.setStorageData(data);

        DataUtilities.setStorageData(
            this,
            data,
            "__type",
            "label",
            "chartType",
            "dataName",
            "allowSeries",
            "seriesNames",
            "stackBars",
            "seriesDataType",
            "xAxisLabel",
            "yAxisLabel",
            "showLegend",
            "legendPosition",
            "render3D",
            "colorType",
            "color",
            "showXYAxis",
            "allowActions",
            "showTooltip",
            "setLowerBounds",
            "lowerBounds",
            "setUpperBounds",
            "upperBounds",
            "numberFormatter",
            "customNumberFormatter",
            "cursor",
            "showIncrements",
            "increment",
        );
    }
}

const StyledContainer = styled(Box)`
    padding: 16px;
    border-radius: 4px;
    border: 1px solid ${UIStyleConstants.GRAY_LIGHTER_COLOR};
`;
